export type ScheduleViewWeekStart = 'sunday' | 'monday';
export type ScheduleViewDensity = 'standard' | 'compact';
export type PersonalThemeOptions = {
  mode: 'light' | 'dark';
  navigationLayout: 'vertical' | 'horizontal';
  pageStyle: 'regular' | 'classic' | 'modern';
  width: 'fullwidth' | 'boxed';
  primaryRgb: string;
};

export type ScheduleViewPreferences = {
  weekStart: ScheduleViewWeekStart;
  density: ScheduleViewDensity;
  pinnedScheduleIds: string[] | null;
  themeOptions: PersonalThemeOptions | null;
  lastSelectedCompanySlug: string | null;
  lastSelectedGroupSlug: string | null;
  lastSelectedScheduleId: string | null;
};

export const defaultScheduleViewPreferences: ScheduleViewPreferences = {
  weekStart: 'sunday',
  density: 'standard',
  pinnedScheduleIds: null,
  themeOptions: null,
  lastSelectedCompanySlug: null,
  lastSelectedGroupSlug: null,
  lastSelectedScheduleId: null,
};

const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? '';

const mapPreferences = (payload: { week_start: ScheduleViewWeekStart; density: ScheduleViewDensity; pinned_schedule_ids?: string[] | null; theme_options?: { mode: 'light' | 'dark'; navigation_layout: 'vertical' | 'horizontal'; page_style: 'regular' | 'classic' | 'modern'; width: 'fullwidth' | 'boxed'; primary_rgb: string } | null; last_selected_company_slug?: string | null; last_selected_group_slug?: string | null; last_selected_schedule_id?: string | null }): ScheduleViewPreferences => ({
  weekStart: payload.week_start,
  density: payload.density,
  pinnedScheduleIds: Array.isArray(payload.pinned_schedule_ids) ? payload.pinned_schedule_ids : null,
  themeOptions: payload.theme_options ? {
    mode: payload.theme_options.mode,
    navigationLayout: payload.theme_options.navigation_layout,
    pageStyle: payload.theme_options.page_style,
    width: payload.theme_options.width,
    primaryRgb: payload.theme_options.primary_rgb,
  } : null,
  lastSelectedCompanySlug: payload.last_selected_company_slug ?? null,
  lastSelectedGroupSlug: payload.last_selected_group_slug ?? null,
  lastSelectedScheduleId: payload.last_selected_schedule_id ?? null,
});

export const fetchScheduleViewPreferences = async (signal?: AbortSignal): Promise<ScheduleViewPreferences> => {
  const response = await fetch('/api/v1/secure/profile/schedule-view-preferences', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Schedule view preferences request failed (${response.status}).`);
  }

  return mapPreferences(await response.json());
};

export const updateScheduleViewPreferences = async (preferences: ScheduleViewPreferences): Promise<ScheduleViewPreferences> => {
  const response = await fetch('/api/v1/secure/profile/schedule-view-preferences', {
    method: 'PUT',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
    body: JSON.stringify({
      week_start: preferences.weekStart,
      density: preferences.density,
      pinned_schedule_ids: preferences.pinnedScheduleIds,
      theme_options: preferences.themeOptions ? {
        mode: preferences.themeOptions.mode,
        navigation_layout: preferences.themeOptions.navigationLayout,
        page_style: preferences.themeOptions.pageStyle,
        width: preferences.themeOptions.width,
        primary_rgb: preferences.themeOptions.primaryRgb,
      } : null,
      last_selected_company_slug: preferences.lastSelectedCompanySlug,
      last_selected_group_slug: preferences.lastSelectedGroupSlug,
      last_selected_schedule_id: preferences.lastSelectedScheduleId,
    }),
  });

  if (!response.ok) {
    throw new Error(`Unable to save schedule view preferences (${response.status}).`);
  }

  return mapPreferences(await response.json());
};

export const updateLastSelectedScheduleGroup = async (companySlug: string, groupSlug: string): Promise<void> => {
  const response = await fetch('/api/v1/secure/profile/schedule-view-preferences/scope', {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
    body: JSON.stringify({
      last_selected_company_slug: companySlug,
      last_selected_group_slug: groupSlug,
    }),
  });

  if (!response.ok) {
    throw new Error(`Unable to remember the selected Schedule Group (${response.status}).`);
  }
};

export const updateLastSelectedSchedule = async (scheduleId: string): Promise<void> => {
  const response = await fetch('/api/v1/secure/profile/schedule-view-preferences/scope', {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken,
    },
    body: JSON.stringify({ last_selected_schedule_id: scheduleId }),
  });

  if (!response.ok) {
    throw new Error(`Unable to remember the selected schedule (${response.status}).`);
  }
};
